import { useState, useContext } from 'react';

import {
  Button,
  Combobox,
  Group,
  Checkbox,
  Text,
  useCombobox,
  ScrollAreaAutosize,
} from '@mantine/core';
import { MenuMappingContext } from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMapping';
import { ChevronDownIconXs } from '@chownow/pantry/icons';

interface FilterGroupProps {
  handleFilterChange: (label: string, values: string[]) => void;
  label: string;
  options: {
    text: string;
    value: string | string[];
  }[];
}

export default function FilterGroup({ handleFilterChange, label, options }: FilterGroupProps) {
  const combobox = useCombobox();
  const [value, setValue] = useState<string[]>([]);
  const [groupLabel, setGroupLabel] = useState<string>(`All ${label}`);
  const { setCheckedItems } = useContext(MenuMappingContext);

  const allChecked = options.every(option => value.includes(option.text));

  const onAllClick = () => {
    if (allChecked) {
      setValue([]);
    } else {
      setValue(options.map(option => option.text));
    }
  };

  const onOptionClick = (optionValue: string) => {
    if (value.includes(optionValue)) {
      setValue(value.filter(v => v !== optionValue));
    } else {
      setValue([...value, optionValue]);
    }
  };

  function updateGroupLabel() {
    if (value.length === 1) {
      setGroupLabel(value[0]);
      return;
    }

    if (value.length > 1 && value.length < options.length) {
      setGroupLabel(`${value.length} ${label}`);
      return;
    }

    setGroupLabel(`All ${label}`);
  }

  const onDoneClick = () => {
    setCheckedItems([]);
    updateGroupLabel();
    const values = value
      .map(v => options.find(option => option.text === v)?.value)
      .filter((val): val is string | string[] => val !== undefined)
      .flat();
    handleFilterChange(label, values);
    combobox.closeDropdown();
  };

  const hasOptions = options && options.length > 0;

  return (
    <Combobox store={combobox} position="bottom-start">
      <Combobox.Target>
        <Button
          key={`${label}_btn`}
          variant="ghost"
          size="sm"
          onClick={() => hasOptions && combobox.openDropdown()}
          onFocus={() => hasOptions && combobox.openDropdown()}
          disabled={!hasOptions}
        >
          <Group gap="xs2">
            <Text variant="caption-xs" truncate="end">
              {hasOptions ? groupLabel : `Loading ${label}...`}
            </Text>
            <ChevronDownIconXs />
          </Group>
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown miw="280">
        <ScrollAreaAutosize type="scroll" mah="300">
          <Combobox.Options>
            <Combobox.Option
              value="all"
              px="sm"
              py={10}
              style={{
                background: 'transparent',
                cursor: 'default',
              }}
              data-hover={false}
              data-active={false}
            >
              <Group gap="xs2">
                <Checkbox checked={allChecked} onChange={onAllClick} tabIndex={-1} />
                <Text style={{ cursor: 'text' }}>{`All ${label}`}</Text>
              </Group>
            </Combobox.Option>
            {options.map(option => (
              <Combobox.Option
                value={Array.isArray(option.value) ? option.value.join(',') : option.value}
                key={`${option.text}_option`}
                px="sm"
                py={10}
                style={{
                  background: 'transparent',
                  cursor: 'default',
                }}
                data-hover={false}
                data-active={false}
              >
                <Group gap="xs2">
                  <Checkbox
                    checked={value.includes(option.text)}
                    onChange={() => onOptionClick(option.text)}
                    tabIndex={-1}
                  />
                  <Text style={{ cursor: 'text' }}>{option.text}</Text>
                </Group>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </ScrollAreaAutosize>
        <Button size="sm" fullWidth mt="sm" onClick={onDoneClick}>
          Done
        </Button>
      </Combobox.Dropdown>
    </Combobox>
  );
}
